import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FileText, Plus, Edit3, Trash2, ExternalLink, Save, X, Loader2, Search, CheckCircle2, Circle, Clock, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = () => ({
  id: null,
  title: '',
  slug: '',
  meta_description: '',
  body: '',
  header_image: '',
  header_image_alt: '',
  tags: '',
  status: 'draft',
  scheduled_publish_at: '',
  author: 'Connectify',
});

// Auto-slugify for the live preview so admins see the URL as they type.
const slugify = (text) => (text || '')
  .toLowerCase()
  .replace(/[^a-z0-9\s-]/g, '')
  .replace(/[\s_-]+/g, '-')
  .replace(/^-|-$/g, '')
  .slice(0, 80);

// Strip HTML tags for excerpt/reading-time helpers.
const stripHtml = (html) => (html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [search, setSearch] = useState('');
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');
  const [uploading, setUploading] = useState(false);

  // Upload a local image file to the backend and set the resulting URL as
  // the post's header image. Uses the shared /api/admin/upload-image
  // endpoint (5 MB limit, persists in Mongo + disk).
  const uploadHeaderImage = async (file) => {
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setErr(`Unsupported file type: ${file.type}. Use PNG, JPG, GIF, or WEBP.`);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErr('File too large — max 5 MB.');
      return;
    }
    setErr('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/admin/upload-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data?.success && data?.url) {
        setForm((f) => ({
          ...f,
          header_image: data.url,
          // Default the alt text to the file name (minus extension) if the
          // admin hasn't set one yet — better for SEO than leaving it blank.
          header_image_alt:
            f.header_image_alt ||
            file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim(),
        }));
        setToast('Image uploaded');
        setTimeout(() => setToast(''), 2500);
      } else {
        setErr('Upload succeeded but no URL returned.');
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const loadPosts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/blog`);
      if (data?.success) setPosts(data.posts || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPosts(); }, []);

  const openNew = () => {
    setForm(empty());
    setErr('');
    setEditorOpen(true);
  };

  const openEdit = async (postId) => {
    setErr('');
    try {
      const { data } = await axios.get(`${API}/admin/blog/${postId}`);
      if (data?.success) {
        const p = data.post;
        // Convert UTC ISO → local <input type="datetime-local"> value.
        let localDt = '';
        if (p.scheduled_publish_at) {
          try {
            const d = new Date(p.scheduled_publish_at);
            const off = d.getTimezoneOffset();
            const local = new Date(d.getTime() - off * 60000);
            localDt = local.toISOString().slice(0, 16);
          } catch (_) { /* ignore */ }
        }
        setForm({
          id: p.id,
          title: p.title || '',
          slug: p.slug || '',
          meta_description: p.meta_description || '',
          body: p.body || '',
          header_image: p.header_image || '',
          header_image_alt: p.header_image_alt || '',
          tags: (p.tags || []).join(', '),
          status: p.status || 'draft',
          scheduled_publish_at: localDt,
          author: p.author || 'Connectify',
        });
        setEditorOpen(true);
      }
    } catch (e) {
      setErr('Failed to load post');
    }
  };

  const save = async () => {
    setErr('');
    if (!form.title.trim()) { setErr('Title is required'); return; }
    if (form.status === 'scheduled') {
      if (!form.scheduled_publish_at) { setErr('Pick a date & time to schedule the post.'); return; }
      const when = new Date(form.scheduled_publish_at);
      if (isNaN(when.getTime())) { setErr('Invalid scheduled date/time.'); return; }
      if (when.getTime() <= Date.now()) { setErr('Scheduled time must be in the future.'); return; }
    }
    setSaving(true);
    try {
      // Convert the local datetime-local value → UTC ISO for the backend.
      let scheduledIso = null;
      if (form.status === 'scheduled' && form.scheduled_publish_at) {
        scheduledIso = new Date(form.scheduled_publish_at).toISOString();
      }
      const payload = {
        title: form.title.trim(),
        slug: form.slug.trim(),
        meta_description: form.meta_description.trim(),
        body: form.body,
        header_image: form.header_image.trim(),
        header_image_alt: form.header_image_alt.trim(),
        tags: form.tags,
        status: form.status,
        scheduled_publish_at: scheduledIso,
        author: form.author.trim() || 'Connectify',
      };
      if (form.id) {
        await axios.put(`${API}/admin/blog/${form.id}`, payload);
        setToast('Post updated');
      } else {
        await axios.post(`${API}/admin/blog`, payload);
        setToast('Post created');
      }
      setEditorOpen(false);
      await loadPosts();
      setTimeout(() => setToast(''), 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (postId, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/blog/${postId}`);
      setToast('Post deleted');
      await loadPosts();
      setTimeout(() => setToast(''), 2500);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return posts;
    return posts.filter(p =>
      (p.title || '').toLowerCase().includes(q) ||
      (p.slug || '').toLowerCase().includes(q) ||
      (p.tags || []).join(',').toLowerCase().includes(q)
    );
  }, [posts, search]);

  const previewSlug = form.slug.trim() ? slugify(form.slug) : slugify(form.title);
  const previewMeta = form.meta_description.trim() || stripHtml(form.body).slice(0, 155) + (stripHtml(form.body).length > 155 ? '…' : '');
  const previewWords = stripHtml(form.body).split(/\s+/).filter(Boolean).length;
  const previewReadTime = Math.max(1, Math.round(previewWords / 220));

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="admin-blog-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2">
            <FileText size={22} /> Blog Manager
          </h2>
          <p className="text-sm text-gray-500 mt-1">Paste content from opinly.ai (or write your own). Auto-generated slugs, meta descriptions, and structured data for SEO.</p>
        </div>
        <button
          onClick={openNew}
          data-testid="new-blog-post-btn"
          className="flex items-center gap-2 bg-black text-white px-4 py-2.5 rounded-xl font-bold hover:bg-gray-800"
        >
          <Plus size={18} /> New Post
        </button>
      </div>

      {toast && (
        <div className="mb-4 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          <CheckCircle2 size={16} /> {toast}
        </div>
      )}

      <div className="mb-4 relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search posts by title, slug, or tag"
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm"
          data-testid="blog-search"
        />
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-gray-500">No posts yet. Click <b>New Post</b> to create your first.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                <th className="py-2 pr-4">Title</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Slug</th>
                <th className="py-2 pr-4">Updated</th>
                <th className="py-2 pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b hover:bg-gray-50" data-testid={`blog-row-${p.slug}`}>
                  <td className="py-3 pr-4">
                    <div className="font-semibold text-gray-900 line-clamp-1">{p.title}</div>
                    <div className="text-xs text-gray-500">{p.reading_time_minutes || 1} min • {(p.tags || []).join(', ') || 'no tags'}</div>
                  </td>
                  <td className="py-3 pr-4">
                    {p.status === 'published' ? (
                      <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full"><CheckCircle2 size={12} /> Published</span>
                    ) : p.status === 'scheduled' ? (
                      <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full" title={p.scheduled_publish_at || ''}>
                        <Clock size={12} /> Scheduled
                        {p.scheduled_publish_at && (
                          <span className="ml-1 opacity-80">
                            {new Date(p.scheduled_publish_at).toLocaleString(undefined, {
                              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                            })}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full"><Circle size={12} /> Draft</span>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-xs text-gray-500 font-mono">{p.slug}</td>
                  <td className="py-3 pr-4 text-xs text-gray-500">{p.updated_at ? new Date(p.updated_at).toLocaleDateString() : '—'}</td>
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-2">
                      <a
                        href={`/blog/${p.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-1.5 rounded hover:bg-gray-100"
                        title="View live"
                      >
                        <ExternalLink size={16} className="text-gray-600" />
                      </a>
                      <button onClick={() => openEdit(p.id)} data-testid={`edit-post-${p.slug}`} className="p-1.5 rounded hover:bg-gray-100" title="Edit">
                        <Edit3 size={16} className="text-blue-600" />
                      </button>
                      <button onClick={() => remove(p.id, p.title)} data-testid={`delete-post-${p.slug}`} className="p-1.5 rounded hover:bg-red-50" title="Delete">
                        <Trash2 size={16} className="text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editorOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !saving && setEditorOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="blog-editor-modal">
            <div className="sticky top-0 bg-white z-10 border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-black text-gray-900">
                {form.id ? 'Edit Post' : 'New Post'}
              </h3>
              <button onClick={() => setEditorOpen(false)} className="p-2 rounded hover:bg-gray-100" disabled={saving}>
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}

              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1 block">Title *</label>
                <input
                  data-testid="post-title-input"
                  value={form.title}
                  onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Best eSIM for Japan in 2026"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base font-semibold"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1 block">Slug (URL)</label>
                  <input
                    data-testid="post-slug-input"
                    value={form.slug}
                    onChange={(e) => setForm(f => ({ ...f, slug: e.target.value }))}
                    placeholder={previewSlug || 'auto-from-title'}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono"
                  />
                  <div className="text-xs text-gray-500 mt-1">/blog/<b>{previewSlug || '(auto)'}</b></div>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1 block">Status</label>
                  <select
                    data-testid="post-status-select"
                    value={form.status}
                    onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  >
                    <option value="draft">Draft (hidden)</option>
                    <option value="scheduled">Scheduled (auto-publish at time)</option>
                    <option value="published">Published (live + in sitemap)</option>
                  </select>
                </div>
              </div>

              {form.status === 'scheduled' && (
                <div>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1 block">
                    Publish at (your local timezone)
                  </label>
                  <input
                    data-testid="post-scheduled-input"
                    type="datetime-local"
                    value={form.scheduled_publish_at}
                    onChange={(e) => setForm(f => ({ ...f, scheduled_publish_at: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    The post will automatically go live at this time (checked every 60 s server-side).
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1 block">Meta description (SEO)</label>
                <textarea
                  data-testid="post-meta-desc"
                  value={form.meta_description}
                  onChange={(e) => setForm(f => ({ ...f, meta_description: e.target.value }))}
                  placeholder="Leave blank to auto-generate from the first 155 chars of the body."
                  rows={2}
                  maxLength={300}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <div className="text-xs text-gray-500 mt-1">{form.meta_description.length}/300 chars</div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1 block">Header image</label>
                  <div className="flex items-stretch gap-2">
                    <input
                      data-testid="post-header-image"
                      value={form.header_image}
                      onChange={(e) => setForm(f => ({ ...f, header_image: e.target.value }))}
                      placeholder="Paste URL or click Upload…"
                      className="flex-1 min-w-0 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                    <label
                      data-testid="upload-header-image-btn"
                      className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-sm font-semibold cursor-pointer hover:bg-gray-50 ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
                      title="Upload from your computer (PNG, JPG, GIF, or WEBP up to 5 MB)"
                    >
                      {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                      {uploading ? 'Uploading…' : 'Upload'}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files && e.target.files[0];
                          uploadHeaderImage(file);
                          // Reset the input so re-selecting the same file still fires onChange.
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Paste an external URL <b>or</b> upload from your computer (max 5 MB).
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1 block">Image alt text (accessibility + SEO)</label>
                  <input
                    data-testid="post-header-alt"
                    value={form.header_image_alt}
                    onChange={(e) => setForm(f => ({ ...f, header_image_alt: e.target.value }))}
                    placeholder="Defaults to the post title if empty"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              </div>

              {form.header_image && (
                <img
                  src={form.header_image}
                  alt={form.header_image_alt || form.title}
                  className="rounded-xl w-full max-h-64 object-cover border border-gray-200"
                />
              )}

              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1 block">Tags (comma-separated)</label>
                <input
                  data-testid="post-tags"
                  value={form.tags}
                  onChange={(e) => setForm(f => ({ ...f, tags: e.target.value }))}
                  placeholder="Japan, eSIM Guide, 2026"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1 block flex items-center justify-between">
                  <span>Body (HTML or Markdown) *</span>
                  <span className="text-gray-400 font-normal normal-case">Paste directly from opinly.ai — HTML tags supported</span>
                </label>
                <textarea
                  data-testid="post-body"
                  value={form.body}
                  onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))}
                  placeholder="<h2>Section heading</h2>&#10;<p>Paragraph text with <a href='...'>links</a>.</p>&#10;<ul><li>List items</li></ul>"
                  rows={18}
                  className="w-full px-3 py-3 border border-gray-200 rounded-lg text-sm font-mono leading-relaxed"
                />
                <div className="text-xs text-gray-500 mt-1">
                  {previewWords.toLocaleString()} words • ~{previewReadTime} min read
                </div>
              </div>

              {/* Google SERP preview */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <div className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">Google SERP preview</div>
                <div className="bg-white p-3 rounded border border-gray-200" data-testid="serp-preview">
                  <div className="text-xs text-emerald-700 mb-1">connectify-esims.com › blog › {previewSlug || '(slug)'}</div>
                  <div className="text-blue-800 text-lg leading-tight hover:underline cursor-default line-clamp-2">
                    {form.title || 'Your post title'} | Connectify eSIMs
                  </div>
                  <div className="text-sm text-gray-700 line-clamp-2 mt-1">
                    {previewMeta || 'Meta description will appear here…'}
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={() => setEditorOpen(false)}
                disabled={saving}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="save-post-btn"
                className="flex items-center gap-2 bg-black text-white px-5 py-2 rounded-lg font-bold hover:bg-gray-800 disabled:opacity-60"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {form.id ? 'Update' : 'Create'} {form.status === 'published' ? 'and publish' : form.status === 'scheduled' ? 'and schedule' : 'as draft'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManager;
